/**
 * Frontend API client for clinician-gap server persistence.
 */
import apiClient from "@shared/services/apiClient";
import type { PatientSeizureTemplate } from "../utils/detectorPerfAndSeizureTemplates";
import type { ConsensusQueueItem, IcuEscalationEvent } from "../utils/consensusAndIcuEscalation";
import type { QeegRawLinkedLayout } from "../utils/qeegRawLinkedLayout";
import {
  createDefaultQeegRawLayout,
  persistLayoutKey,
} from "../utils/qeegRawLinkedLayout";

function unwrap<T>(response: any): T {
  return (response?.data?.data ?? response?.data) as T;
}

export async function listPatientSeizureTemplatesApi(params?: {
  patientId?: string;
  admissionId?: string;
}): Promise<any[]> {
  const response = await apiClient.get("/eeg/patient-seizure-templates", {
    params,
  });
  const data = unwrap<any[]>(response);
  return Array.isArray(data) ? data : [];
}

export async function upsertPatientSeizureTemplateApi(input: {
  patientId: string;
  admissionId: string;
  studyId?: string | null;
  templateId?: string | null;
  payload: PatientSeizureTemplate | Record<string, unknown>;
}): Promise<any> {
  const response = await apiClient.post("/eeg/patient-seizure-templates", input);
  return unwrap(response);
}

export async function getConsensusQueueItemApi(
  studyId: string,
): Promise<ConsensusQueueItem | null> {
  const response = await apiClient.get(
    `/eeg/studies/${studyId}/consensus-queue`,
  );
  const row = unwrap<any>(response);
  return (row?.payload_json as ConsensusQueueItem) || row || null;
}

export async function upsertConsensusQueueItemApi(
  studyId: string,
  payload: ConsensusQueueItem,
  reportId?: string | null,
): Promise<any> {
  const response = await apiClient.put(
    `/eeg/studies/${studyId}/consensus-queue`,
    { payload, reportId },
  );
  return unwrap(response);
}

export async function loadStudyReviewLayout(input: {
  studyId: string;
  userId: string;
}): Promise<QeegRawLinkedLayout> {
  const localKey = persistLayoutKey(input.studyId, input.userId);
  try {
    const response = await apiClient.get(
      `/eeg/studies/${input.studyId}/review-layout`,
    );
    const row = unwrap<any>(response);
    const layout = (row?.layout_json || row) as Partial<QeegRawLinkedLayout> | null;
    if (layout && typeof layout === "object") {
      const merged = createDefaultQeegRawLayout(layout);
      try {
        localStorage.setItem(localKey, JSON.stringify(merged));
      } catch {
        /* ignore quota */
      }
      return merged;
    }
  } catch {
    /* fall through to local */
  }
  try {
    const raw = localStorage.getItem(localKey);
    if (raw) {
      return createDefaultQeegRawLayout(JSON.parse(raw));
    }
  } catch {
    /* ignore */
  }
  return createDefaultQeegRawLayout();
}

export async function saveStudyReviewLayout(input: {
  studyId: string;
  userId: string;
  layout: QeegRawLinkedLayout;
}): Promise<QeegRawLinkedLayout> {
  const localKey = persistLayoutKey(input.studyId, input.userId);
  try {
    localStorage.setItem(localKey, JSON.stringify(input.layout));
  } catch {
    /* ignore */
  }
  try {
    const response = await apiClient.put(
      `/eeg/studies/${input.studyId}/review-layout`,
      { layout: input.layout },
    );
    const row = unwrap<any>(response);
    const layout = (row?.layout_json || input.layout) as QeegRawLinkedLayout;
    return createDefaultQeegRawLayout(layout);
  } catch {
    return input.layout;
  }
}

export async function listIcuEscalationsApi(
  studyId: string,
): Promise<IcuEscalationEvent[]> {
  const response = await apiClient.get(
    `/eeg/studies/${studyId}/icu-escalations`,
  );
  const rows = unwrap<any[]>(response);
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => {
    const payload = (row.payload_json || {}) as Partial<IcuEscalationEvent>;
    return {
      eventId: String(row.event_id || payload.eventId || ""),
      studyId: String(row.study_id || payload.studyId || studyId),
      kind: (payload.kind || "qeeg_alert_confirmation") as IcuEscalationEvent["kind"],
      requiresConfirmation: Boolean(
        row.requires_confirmation ?? payload.requiresConfirmation ?? true,
      ),
      confirmed: Boolean(row.confirmed ?? payload.confirmed),
      confirmedBy: row.confirmed_by || payload.confirmedBy || null,
      message: String(payload.message || "ICU escalation"),
      atIso: String(
        payload.atIso ||
          row.created_at ||
          new Date().toISOString(),
      ),
      schemaVersion: payload.schemaVersion || "icu-escalation-v1.0.0",
    };
  });
}

export async function confirmIcuEscalationApi(eventId: string): Promise<any> {
  const response = await apiClient.post(
    `/eeg/icu-escalations/${eventId}/confirm`,
  );
  return unwrap(response);
}

export async function createIcuEscalationApi(
  studyId: string,
  event: IcuEscalationEvent,
): Promise<any> {
  const response = await apiClient.post(
    `/eeg/studies/${studyId}/icu-escalations`,
    {
      requiresConfirmation: event.requiresConfirmation,
      payload: event,
    },
  );
  return unwrap(response);
}
